import { useEffect, useState, useCallback } from 'react';
import { Header, Section, useToast } from '../../shared/components';
import { useCurrentUser } from '../../shared/auth/AuthProvider';
import { getOperationTimesRepository } from '../../shared/services/operationTimes/repositoryFactory';
import { getSettingsRepository } from '../../shared/services/settings/repositoryFactory';
import { OperationTimeRecord } from '../../shared/services/operationTimes/types';
import { ParamActivity, ParamUser } from '../../shared/services/settings/types';
import { ActivityForm } from './components/ActivityForm';
import { ActivityFilters, FiltersState } from './components/ActivityFilters';
import { ActivityHistoryTable } from './components/ActivityHistoryTable';
import { EditRecordModal } from './components/EditRecordModal';
import { DeleteRecordModal } from './components/DeleteRecordModal';

const operationTimesRepo = getOperationTimesRepository();
const settingsRepo = getSettingsRepository();

function todayInputValue(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Modulo: Control de Tiempos Operativos.
 * Vista por defecto: SOLO los registros del usuario en sesion, del dia actual
 * (para ver otros dias/usuarios hay que usar los filtros explicitamente).
 */
export function OperationTimesPage() {
  const user = useCurrentUser();
  const toast = useToast();

  const [activities, setActivities] = useState<ParamActivity[]>([]);
  const [users, setUsers] = useState<ParamUser[]>([]);
  const [records, setRecords] = useState<OperationTimeRecord[]>([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [filters, setFilters] = useState<FiltersState>({ userId: user.id, activityId: '', dateFrom: todayInputValue(), dateTo: todayInputValue(), novedad: '' });
  const [editing, setEditing] = useState<OperationTimeRecord | null>(null);
  const [deleting, setDeleting] = useState<OperationTimeRecord | null>(null);

  // Esta pantalla ya esta bloqueada a "solo mis registros" (ver lockUserFilter
  // en ActivityFilters), asi que cualquier rol puede editar/eliminar aqui - el
  // backend refuerza que un operador solo pueda tocar sus PROPIOS registros
  // (supervisor/admin conservan permiso amplio sobre los de cualquiera).
  const canEdit = true;
  const canDelete = true;

  const loadRecords = useCallback(async () => {
    setIsLoadingRecords(true);
    try {
      const result = await operationTimesRepo.list({
        userId: filters.userId || undefined,
        activityId: filters.activityId || undefined,
        // IMPORTANTE: el Worker de Cloudflare corre siempre en UTC (no tiene zona
        // horaria local). Si aqui enviaramos un string "2026-07-20T00:00:00.000"
        // sin sufijo "Z", el backend lo interpretaria como UTC en vez de la hora
        // LOCAL del usuario, desplazando la ventana de "hoy" por su offset horario
        // (en Colombia, 5 horas) y ocultando registros creados por la noche.
        // new Date(...).toISOString() resuelve la hora local del NAVEGADOR primero,
        // y solo despues la convierte a UTC con "Z" explicito - sin ambiguedad.
        dateFrom: filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`).toISOString() : undefined,
        dateTo: filters.dateTo ? new Date(`${filters.dateTo}T23:59:59.999`).toISOString() : undefined,
        novedad: filters.novedad || undefined,
        page: 1,
        pageSize: 200,
      });
      setRecords(result.items);
    } finally {
      setIsLoadingRecords(false);
    }
  }, [filters]);

  useEffect(() => {
    settingsRepo.listActivities().then(setActivities);
    settingsRepo.listUsers().then(setUsers);
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSave = async (data: { activityId: string; activityName: string; startDateTime: string; observations?: string; pausedSeconds?: number }) => {
    setIsSaving(true);
    try {
      await operationTimesRepo.create({
        userId: user.id,
        userName: user.fullName,
        ...data,
      });
      toast.show('Actividad registrada correctamente.');
      loadRecords();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar el registro.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (id: string, patch: Parameters<typeof operationTimesRepo.update>[1]) => {
    await operationTimesRepo.update(id, patch);
    toast.show('Registro actualizado.');
    loadRecords();
  };

  const handleDelete = async (id: string, reason: string) => {
    await operationTimesRepo.remove(id, reason);
    toast.show('Registro eliminado.');
    loadRecords();
  };

  return (
    <div>
      <Header title="Control de Tiempos Operativos" subtitle="Registra el tiempo dedicado a cada actividad" />
      <div className="space-y-6 p-4">
        <ActivityForm user={user} activities={activities} isSaving={isSaving} onSave={handleSave} />

        <Section title="Historial de registros">
          <div className="mb-4">
            <ActivityFilters value={filters} onChange={setFilters} users={users} activities={activities} lockUserFilter />
          </div>
          <ActivityHistoryTable
            rows={records}
            isLoading={isLoadingRecords}
            canEdit={canEdit}
            canDelete={canDelete}
            onEdit={setEditing}
            onDelete={setDeleting}
          />
        </Section>
      </div>

      <EditRecordModal record={editing} activities={activities} onClose={() => setEditing(null)} onSubmit={handleUpdate} />
      <DeleteRecordModal record={deleting} onClose={() => setDeleting(null)} onConfirm={handleDelete} />
    </div>
  );
}
